// SupplierDisambiguation — inline chat picker for the §5.EV recognition arc (Prompt 4b).
//
// Renders the backend's `action="supplier_disambiguation"` response inline in the
// chat transcript. The 4a backend (helpers/supplier_disambiguation.py +
// routes/ai_chat.py, LOCKED this prompt) decides silent-vs-picker and, when a pick
// is needed, returns `action_data` in one of two shapes:
//
//   action_data.scope === "supplier"        (SINGLE-PICK)
//     action_data.slots = [ { status, reference, candidates: [{supplier_id,
//                            supplier_name, hint:{l12m_spend, material_count}}] } ]
//     -> one suggestion list; TAP a row to resolve immediately (no confirm button).
//
//   action_data.scope === "multi_supplier"  (MULTI-PICK, a comparison)
//     action_data.candidates  = ONE ranked pool [{supplier_id, supplier_name,
//                                hint, pre_selected}]
//     action_data.target_count = how many suppliers the comparison needs
//     -> select exactly target_count from the pool; the slots the backend already
//        resolved cleanly arrive pre_selected; a single "Compare" runs only once
//        target_count are selected (tapping a row only toggles — never fires early).
//
// Both shapes carry action_data.original_query (replayed on selection, carrying the
// chosen supplier_id / supplier_ids so the 4a explicit-id bypass fires and the
// picker never loops) + action_data.search_endpoint ("/api/suppliers/search").
//
// The type-to-search field (per design D2/E6) hits GET /api/suppliers/search; it
// only ever SELECTS an existing supplier (returns an existing supplier_id) — it
// NEVER creates one. Mirrors the §5.BP AsyncSelect precedent (ManageContracts.js).
//
// Design: docs/audits/2026-06-22_recognition-arc_plan.md (D1-D5, Ryan-locked).
// No emojis (design-system rule); SVG icons + design-system tokens only.

import React, { useState, useMemo, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { API_BASE } from '../config';

// Mirrors backend Query(le=50) cap on /api/suppliers/search — keep in lockstep.
const SUPPLIER_SEARCH_LIMIT = 50;
// Mirrors backend Query(le=200) cap on /api/contracts/materials/search — keep in
// lockstep. Lane 734 reuses that endpoint (it is a plain `materials_master` ILIKE
// autocomplete that merely lives under the contracts router; nothing about it is
// contract-scoped) rather than adding a second materials search.
const MATERIAL_SEARCH_LIMIT = 50;

// Design-system tokens (design-system.md) used inline to match Home.js's raw-hex style.
const C = {
  blue: '#2251FF',
  blueLight: '#F0F4FF',
  text: '#111827',
  textSecondary: '#6B7280',
  textMuted: '#8A94A6',
  border: '#E2E5EA',
  borderLight: '#F0F1F3',
  card: '#FFFFFF',
  page: '#F8F9FB',
  successFg: '#059669',
  successBg: '#ECFDF5',
};

// react-select expects loadOptions(input, callback); the callback form avoids the
// Promise-debounce hanging-callback footgun (same note as ManageContracts.js).
function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

function formatSpend(value) {
  const n = Number(value) || 0;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

// Disambiguating hint line (design D2 / design-review Pass 1): always rendered as a
// positive fact even when empty ("$0 L12M / 0 materials") so an absent value never
// reads as a silent omission — the 7 same-prefix Parkers are unpickable by name alone.
function formatHint(l12mSpend, materialCount) {
  const count = Number(materialCount) || 0;
  return `${formatSpend(l12mSpend)} L12M · ${count} material${count === 1 ? '' : 's'}`;
}

// Search-endpoint result -> react-select option. `supplier` carries the normalized
// candidate shape the picker hands back on selection.
function searchResultToOption(r) {
  return {
    value: r.supplier_id,
    label: `${r.supplier_name}: ${formatHint(r.l12m_spend, r.material_count)}`,
    supplier: {
      supplier_id: r.supplier_id,
      supplier_name: r.supplier_name,
      hint: { l12m_spend: r.l12m_spend, material_count: r.material_count },
    },
  };
}

// Lane 734: material search-result -> react-select option. Deliberately NO spend
// hint. The supplier picker needs one because supplier NAMES collide (the 7
// Parkers); `material_number` is the primary key and does not, so a hint would be
// decoration on a row that is already unambiguous.
function materialResultToOption(m) {
  return {
    value: m.material_number,
    label: m.material_name ? `${m.material_number}: ${m.material_name}` : m.material_number,
    material: { material_number: m.material_number, material_name: m.material_name },
  };
}

function candidateToOption(c) {
  return {
    value: c.supplier_id,
    label: `${c.supplier_name}: ${formatHint(c.hint && c.hint.l12m_spend, c.hint && c.hint.material_count)}`,
    supplier: { supplier_id: c.supplier_id, supplier_name: c.supplier_name, hint: c.hint || {} },
  };
}

const selectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base, state) => ({
    ...base,
    minHeight: '44px',
    fontSize: 'var(--font-size-sm)',
    borderColor: state.isFocused ? C.blue : C.border,
    borderRadius: '10px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(34, 81, 255, 0.08)' : 'none',
    ':hover': { borderColor: C.blue },
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 'var(--font-size-sm)' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: 'var(--font-size-sm)',
    backgroundColor: state.isFocused ? C.blueLight : C.card,
    color: C.text,
    cursor: 'pointer',
  }),
  placeholder: (base) => ({ ...base, fontSize: 'var(--font-size-sm)', color: C.textMuted }),
  input: (base) => ({ ...base, fontSize: 'var(--font-size-sm)' }),
};

// Shared "search all suppliers" field. Selecting an option NEVER creates a supplier
// — it hands an existing supplier_id back through `onChoose`.
function SupplierTypeahead({ getAuthHeaders, onChoose, seedOptions, disabled, placeholder }) {
  const loadOptions = useCallback(
    (input, callback) => {
      fetch(
        `${API_BASE}/api/suppliers/search?q=${encodeURIComponent(input || '')}&limit=${SUPPLIER_SEARCH_LIMIT}`,
        { headers: getAuthHeaders() }
      )
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((data) => callback((data.results || []).map(searchResultToOption)))
        .catch(() => callback([]));
    },
    [getAuthHeaders]
  );

  const debouncedLoad = useMemo(() => debounce(loadOptions, 250), [loadOptions]);

  return (
    <AsyncSelect
      loadOptions={debouncedLoad}
      // Seed the menu with the already-loaded candidate pool (zero round-trip);
      // when there are no candidates (not_found), `true` loads top suppliers on open.
      defaultOptions={seedOptions && seedOptions.length ? seedOptions : true}
      cacheOptions
      isClearable
      isDisabled={disabled}
      value={null} // "search to pick" field — never retains a value
      placeholder={placeholder || 'Search all suppliers by name...'}
      onChange={(opt) => { if (opt && opt.supplier) onChoose(opt.supplier); }}
      styles={selectStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      aria-label="Search all suppliers by name"
    />
  );
}

const hintStyle = {
  fontSize: 'var(--font-size-xs)',
  color: C.textMuted,
  marginTop: '2px',
};

const nameStyle = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: C.text,
};

// A non-supplier "special" option row (§5.FK): the material option on a cross-type
// picker, or the portfolio-level option on an agnostic picker. Visually distinct from
// the supplier rows but uses the same tokens (no emoji).
function SpecialOptionRow({ label, sublabel, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        textAlign: 'left', width: '100%', minHeight: '44px', padding: '10px 14px',
        border: `1px dashed ${C.border}`, borderRadius: '10px', backgroundColor: C.page,
        cursor: disabled ? 'default' : 'pointer', transition: 'all 0.15s ease',
      }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.backgroundColor = C.blueLight; } }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.backgroundColor = C.page; }}
    >
      <div style={nameStyle}>{label}</div>
      {sublabel ? <div style={hintStyle}>{sublabel}</div> : null}
    </button>
  );
}

// ── SINGLE-PICK: tap a row to resolve immediately (no confirm button) ──
// §5.FK reuse: the same component renders the group-member picker (Q5), the
// cross-type picker (Q4, via `materialOption`), and the agnostic picker (Q6, via
// `portfolioOption`). `reference` is the typed token echoed back so the backend can
// key the L0 ledger precisely.
function SingleSupplierPicker({ slot, originalQuery, disabled, getAuthHeaders, onPick,
                               reference, materialOption, portfolioOption, contractActive }) {
  const candidates = useMemo(() => (slot && slot.candidates) || [], [slot]);
  const seedOptions = useMemo(() => candidates.map(candidateToOption), [candidates]);

  const choose = (supplier) => {
    if (disabled) return;
    onPick({
      supplierId: supplier.supplier_id,
      echoText: supplier.supplier_name,
      originalQuery,
      reference,
    });
  };

  const chooseMaterial = () => {
    if (disabled || !materialOption || !materialOption.material_number) return;
    const matLabel = materialOption.material_name
      ? `the material "${materialOption.material_name}"`
      : `material ${materialOption.material_number}`;
    onPick({
      materialNumber: materialOption.material_number,
      echoText: matLabel,
      originalQuery,
      reference,
    });
  };

  const choosePortfolio = () => {
    if (disabled) return;
    onPick({ forceGeneral: true, echoText: 'Portfolio-level (all suppliers)', originalQuery });
  };

  return (
    <div style={{ marginTop: '12px' }}>
      {materialOption && materialOption.material_number ? (
        <div style={{ marginBottom: '8px' }}>
          <SpecialOptionRow
            label={materialOption.material_name
              ? `Material: ${materialOption.material_name}`
              : `Material ${materialOption.material_number}`}
            sublabel={`Material ${materialOption.material_number}`}
            disabled={disabled}
            onClick={chooseMaterial}
          />
        </div>
      ) : null}
      {portfolioOption ? (
        <div style={{ marginBottom: '8px' }}>
          <SpecialOptionRow
            label="Portfolio-level answer (across all suppliers)"
            sublabel="Not about one specific supplier"
            disabled={disabled}
            onClick={choosePortfolio}
          />
        </div>
      ) : null}
      {candidates.length > 0 ? (
        <div role="listbox" aria-label="Matching suppliers" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {candidates.map((c) => (
            <button
              key={c.supplier_id}
              type="button"
              role="option"
              aria-selected="false"
              disabled={disabled}
              onClick={() => choose(c)}
              style={{
                textAlign: 'left',
                width: '100%',
                minHeight: '44px',
                padding: '10px 14px',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                backgroundColor: C.card,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.backgroundColor = C.blueLight; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.backgroundColor = C.card; }}
            >
              <div style={nameStyle}>{c.supplier_name}</div>
              <div style={hintStyle}>{formatHint(c.hint && c.hint.l12m_spend, c.hint && c.hint.material_count)}</div>
            </button>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 'var(--font-size-sm)', color: C.textSecondary, marginBottom: '8px' }}>
          I couldn't find a supplier matching that name. Search by name:
        </div>
      )}

      <div style={{ marginTop: candidates.length > 0 ? '12px' : '0' }}>
        {candidates.length > 0 && !contractActive && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted, marginBottom: '6px' }}>
            Don't see it? Search all suppliers:
          </div>
        )}
        {contractActive && candidates.length > 0 ? (
          <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted }}>
            Select one of the verified options above.
          </div>
        ) : (
          <SupplierTypeahead
            getAuthHeaders={getAuthHeaders}
            onChoose={choose}
            seedOptions={seedOptions}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}

// ── MATERIAL PICK (Lane 734): "which PART did you mean?" ──────────────────────
// Ryan's ask, verbatim: *"if it senses that someone's asking about a material
// number, it pops up with a selector. It doesn't list every single one of them
// because it's so long. But it would just have you type it in, and it searches,
// and you click on it."* So there is deliberately NO full list — the candidate
// rows (present only when the buyer typed 2+ real part numbers) plus a
// type-to-search field over `action_data.search_endpoint`.
//
// A SEPARATE component from SupplierTypeahead rather than a parameterisation of
// it: that one hardcodes /api/suppliers/search and maps supplier_id/supplier_name/
// l12m_spend/material_count, and the shipped supplier picker is a live customer
// surface. Materials key on `material_number` and hand back a materialNumber, not
// a supplierId — a different pick shape, so a shared component would have to
// branch on every line that matters.
function MaterialTypeahead({ getAuthHeaders, onChoose, searchEndpoint, seedOptions, disabled }) {
  const loadOptions = useCallback(
    (input, callback) => {
      fetch(
        `${API_BASE}${searchEndpoint}?q=${encodeURIComponent(input || '')}&limit=${MATERIAL_SEARCH_LIMIT}`,
        { headers: getAuthHeaders() }
      )
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((data) => callback((data.results || []).map(materialResultToOption)))
        .catch(() => callback([]));
    },
    [getAuthHeaders, searchEndpoint]
  );

  const debouncedLoad = useMemo(() => debounce(loadOptions, 250), [loadOptions]);

  return (
    <AsyncSelect
      loadOptions={debouncedLoad}
      defaultOptions={seedOptions && seedOptions.length ? seedOptions : true}
      cacheOptions
      isClearable
      isDisabled={disabled}
      value={null}
      placeholder="Search parts by number or name..."
      onChange={(opt) => { if (opt && opt.material) onChoose(opt.material); }}
      styles={selectStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      aria-label="Search parts by number or name"
    />
  );
}

function MaterialPicker({ materials, reference, originalQuery, disabled,
                         getAuthHeaders, onPick, searchEndpoint, contractActive }) {
  const list = useMemo(() => materials || [], [materials]);
  const seedOptions = useMemo(() => list.map(materialResultToOption), [list]);

  const choose = (material) => {
    if (disabled) return;
    onPick({
      materialNumber: material.material_number,
      echoText: material.material_name
        ? `${material.material_number} - ${material.material_name}`
        : `part ${material.material_number}`,
      originalQuery,
      // The L0 ledger key: what the buyer actually typed, so a later re-mention
      // of the same token short-circuits instead of re-asking.
      reference: reference || material.material_number,
    });
  };

  return (
    <div style={{ marginTop: '12px' }}>
      {list.length > 0 ? (
        <div role="listbox" aria-label="Matching parts" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {list.map((m) => (
            <button
              key={m.material_number}
              type="button"
              role="option"
              aria-selected="false"
              disabled={disabled}
              onClick={() => choose(m)}
              style={{
                textAlign: 'left', width: '100%', minHeight: '44px', padding: '10px 14px',
                border: `1px solid ${C.border}`, borderRadius: '10px', backgroundColor: C.card,
                cursor: disabled ? 'default' : 'pointer', transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.backgroundColor = C.blueLight; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.backgroundColor = C.card; }}
            >
              <div style={nameStyle}>{m.material_number}</div>
              {m.material_name ? <div style={hintStyle}>{m.material_name}</div> : null}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ marginTop: list.length > 0 ? '12px' : '0' }}>
        {list.length > 0 && !contractActive && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted, marginBottom: '6px' }}>
            Don't see it? Search all parts:
          </div>
        )}
        {contractActive && list.length > 0 ? (
          <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted }}>
            Select one of the verified parts above.
          </div>
        ) : (
          <MaterialTypeahead
            getAuthHeaders={getAuthHeaders}
            onChoose={choose}
            searchEndpoint={searchEndpoint}
            seedOptions={seedOptions}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}

// Natural-language join for the transcript echo ("X", "X and Y", "X, Y, and Z").
function joinNames(names) {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

// ── MULTI-PICK (comparison): select exactly target_count from one ranked pool. ──
// The backend pre-marks the clean (verbatim-typed) slots `pre_selected`; they start
// checked. Tapping a row only TOGGLES — the single "Compare" action is gated on
// exactly target_count selected so it never fires early.
function MultiSupplierPicker({ candidates, targetCount, originalQuery, disabled, getAuthHeaders, onPick, contractActive }) {
  // Pool may grow when the user adds a supplier via type-to-search.
  const [pool, setPool] = useState(() => candidates || []);
  const [selectedIds, setSelectedIds] = useState(
    () => new Set((candidates || []).filter((c) => c.pre_selected).map((c) => c.supplier_id))
  );

  const seedOptions = useMemo(() => pool.map(candidateToOption), [pool]);

  const toggle = (supplierId) => {
    if (disabled) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(supplierId)) next.delete(supplierId);
      else next.add(supplierId);
      return next;
    });
  };

  const addFromSearch = (supplier) => {
    if (disabled) return;
    setPool((prev) => (prev.some((c) => c.supplier_id === supplier.supplier_id) ? prev : [...prev, supplier]));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.add(supplier.supplier_id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;
  const canCompare = !disabled && targetCount > 0 && selectedCount === targetCount;

  const compare = () => {
    if (!canCompare) return;
    // Preserve display order (pre_selected / ranked first, search-adds last).
    const chosen = pool.filter((c) => selectedIds.has(c.supplier_id));
    const supplierIds = chosen.map((c) => c.supplier_id);
    const supplierNames = chosen.map((c) => c.supplier_name);
    onPick({ supplierIds, echoText: joinNames(supplierNames), originalQuery });
  };

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', fontWeight: 'var(--font-weight-semibold)' }}>
        Select {targetCount} to compare ({selectedCount} of {targetCount} selected)
      </div>

      <div role="group" aria-label="Suppliers to compare" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {pool.map((c) => {
          const checked = selectedIds.has(c.supplier_id);
          return (
            <button
              key={c.supplier_id}
              type="button"
              role="checkbox"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => toggle(c.supplier_id)}
              style={{
                textAlign: 'left',
                width: '100%',
                minHeight: '44px',
                padding: '10px 14px',
                border: `1px solid ${checked ? C.blue : C.border}`,
                borderRadius: '10px',
                backgroundColor: checked ? C.blueLight : C.card,
                cursor: disabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'all 0.15s ease',
              }}
            >
              {/* checkbox indicator (SVG, no emoji) */}
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  width: '18px',
                  height: '18px',
                  borderRadius: '4px',
                  border: `1.5px solid ${checked ? C.blue : C.textMuted}`,
                  backgroundColor: checked ? C.blue : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {checked && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ ...nameStyle, display: 'block' }}>{c.supplier_name}</span>
                <span style={{ ...hintStyle, display: 'block' }}>{formatHint(c.hint && c.hint.l12m_spend, c.hint && c.hint.material_count)}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: '12px' }}>
        {contractActive ? (
          <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted }}>
            Select only from the verified options above.
          </div>
        ) : (
          <>
        <div style={{ fontSize: 'var(--font-size-xs)', color: C.textMuted, marginBottom: '6px' }}>
          Don't see one? Search all suppliers:
        </div>
        <SupplierTypeahead
          getAuthHeaders={getAuthHeaders}
          onChoose={addFromSearch}
          seedOptions={seedOptions}
          disabled={disabled}
        />
          </>
        )}
      </div>

      <button
        type="button"
        onClick={compare}
        disabled={!canCompare}
        style={{
          marginTop: '14px',
          width: '100%',
          minHeight: '44px',
          padding: '12px 16px',
          borderRadius: '10px',
          border: 'none',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          cursor: canCompare ? 'pointer' : 'not-allowed',
          backgroundColor: canCompare ? C.blue : C.borderLight,
          color: canCompare ? '#FFFFFF' : C.textMuted,
          transition: 'all 0.15s ease',
        }}
      >
        {canCompare ? 'Compare' : `Select ${targetCount} to compare`}
      </button>
    </div>
  );
}

// Resolved state: the picker collapses to a muted confirmation once the user has
// picked (controls hidden; prevents a second submit). The selection ALSO echoes as
// a user turn in the transcript (handled by the parent) per design D5.
function ResolvedNote({ echoText }) {
  return (
    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--font-size-xs)', color: C.textSecondary }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.successFg} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
      <span>Selected: {echoText}</span>
    </div>
  );
}

export default function SupplierDisambiguation({ actionData, resolved, state = 'pending', disabled, getAuthHeaders, onPick }) {
  if (!actionData) return null;

  if (state === 'resolved' && resolved) return <ResolvedNote echoText={resolved} />;

  const originalQuery = actionData.original_query || '';

  // §5.FK cross-type (Q4): supplier candidates + a first-class "the material" row.
  if (actionData.kind === 'cross_type') {
    return (
      <SingleSupplierPicker
        slot={{ candidates: actionData.candidates || [] }}
        originalQuery={originalQuery}
        disabled={disabled}
        getAuthHeaders={getAuthHeaders}
        onPick={onPick}
        reference={actionData.reference}
        materialOption={actionData.material_option}
        contractActive={actionData.clarification_enforced}
      />
    );
  }

  // Lane 734 material_pick: which PART did you mean, or search for one. The
  // zero-candidate shape is a first-class state (the buyer named a part number
  // this tenant does not have), not an error — it renders the search field alone.
  if (actionData.kind === 'material_pick') {
    return (
      <MaterialPicker
        materials={actionData.materials || []}
        reference={actionData.reference}
        originalQuery={originalQuery}
        disabled={disabled}
        getAuthHeaders={getAuthHeaders}
        onPick={onPick}
        searchEndpoint={actionData.search_endpoint || '/api/contracts/materials/search'}
        contractActive={actionData.clarification_enforced}
      />
    );
  }

  // §5.FK group-member (Q5): which entity within the matched group.
  if (actionData.kind === 'group_member') {
    return (
      <SingleSupplierPicker
        slot={{ candidates: actionData.candidates || [] }}
        originalQuery={originalQuery}
        disabled={disabled}
        getAuthHeaders={getAuthHeaders}
        onPick={onPick}
        reference={actionData.reference}
        contractActive={actionData.clarification_enforced}
      />
    );
  }

  // §5.FK agnostic (Q6): portfolio-level vs the named supplier.
  if (actionData.kind === 'agnostic_named') {
    return (
      <SingleSupplierPicker
        slot={{ candidates: actionData.candidates || [] }}
        originalQuery={originalQuery}
        disabled={disabled}
        getAuthHeaders={getAuthHeaders}
        onPick={onPick}
        reference={actionData.reference}
        portfolioOption
        contractActive={actionData.clarification_enforced}
      />
    );
  }

  if (actionData.scope === 'multi_supplier') {
    return (
      <MultiSupplierPicker
        candidates={actionData.candidates || []}
        targetCount={actionData.target_count || 2}
        originalQuery={originalQuery}
        disabled={disabled}
        getAuthHeaders={getAuthHeaders}
        onPick={onPick}
        contractActive={actionData.clarification_enforced}
      />
    );
  }

  // Single supplier: action_data.slots = [ one slot ].
  // §routing-fix P5 Change 2: pass portfolioOption so the plain supplier picker gets the
  // "Portfolio-level answer (across all suppliers)" escape row (choosePortfolio ->
  // onPick({forceGeneral:true})). The backend sets action_data.portfolio_option only when
  // ROUTING_FIX_ENABLED is on (supplier_disambiguation.py build_disambiguation_payload else
  // branch); when absent it is undefined -> SingleSupplierPicker renders no escape row, so
  // this is byte-identical to today when the flag is off. See
  // docs/audits/2026-06-24_routing-fix_design.md.
  const slot = (actionData.slots && actionData.slots[0]) || { candidates: [] };
  return (
    <SingleSupplierPicker
      slot={slot}
      originalQuery={originalQuery}
      disabled={disabled}
      getAuthHeaders={getAuthHeaders}
      onPick={onPick}
      portfolioOption={actionData.portfolio_option}
      contractActive={actionData.clarification_enforced}
    />
  );
}
